import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  useEffect,
} from 'react';

/**
 * GeoContext centralizes ALL geolocation permission + coordinates flow.
 * Components must call `enableLocation()` to request permission.
 * Consumers read `coords` ({ lat, lng }) exclusively from this provider.
 */
const GeoContext = createContext(null);

const LAST_COORDS_KEY = 'last_coords'; // session-scoped

export function GeoProvider({ children }) {
  // Seed coords from session so feed can render immediately after refresh
  const [coords, setCoords] = useState(() => {
    try {
      const raw = sessionStorage.getItem(LAST_COORDS_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed.lat === 'number' && typeof parsed.lng === 'number') {
        return { lat: parsed.lat, lng: parsed.lng };
      }
    } catch (_) {}
    return null;
  });

  // checking -> we are querying Permissions API on mount
  const [status, setStatus] = useState('checking'); // checking | idle | requesting | granted | denied | error
  const [error, setError] = useState(null);
  const [bootstrapped, setBootstrapped] = useState(false);
  const [promptAllowed, setPromptAllowed] = useState(false); // server-driven flag (default: disabled)

  const saveCoords = useCallback((lat, lng) => {
    setCoords({ lat, lng });
    try {
      sessionStorage.setItem(LAST_COORDS_KEY, JSON.stringify({ lat, lng, ts: Date.now() }));
    } catch (_) {}
  }, []);

  const enableLocation = useCallback(async () => {
    if (!promptAllowed) {
      // Globally disabled via server config; do not prompt the browser.
      setStatus((s) => (s === 'granted' ? s : 'idle'));
      return { ok: false, error: new Error('Geolocation prompt disabled') };
    }
    if (status === 'requesting') return;
    setStatus('requesting');
    setError(null);

    if (typeof window === 'undefined' || !('geolocation' in navigator)) {
      const err = new Error('Geolocation is not supported by this browser.');
      setStatus('error');
      setError(err);
      return { ok: false, error: err };
    }

    return new Promise((resolve) => {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const lat = pos.coords.latitude;
          const lng = pos.coords.longitude;
          saveCoords(lat, lng);
          setStatus('granted');
          resolve({ ok: true, coords: { lat, lng } });
        },
        (err) => {
          setStatus(err.code === 1 ? 'denied' : 'error');
          setError(err);
          resolve({ ok: false, error: err });
        },
        { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
      );
    });
  }, [promptAllowed, saveCoords, status]);

  // Force refresh location regardless of cached coords/status
  const refreshLocation = useCallback(async () => {
    if (!promptAllowed) {
      // Respect global disable and avoid calling getCurrentPosition.
      return { ok: false, error: new Error('Geolocation prompt disabled') };
    }
    setStatus('requesting');
    setError(null);
    if (typeof window === 'undefined' || !('geolocation' in navigator)) {
      const err = new Error('Geolocation is not supported by this browser.');
      setStatus('error');
      setError(err);
      return { ok: false, error: err };
    }
    return new Promise((resolve) => {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const lat = pos.coords.latitude;
          const lng = pos.coords.longitude;
          saveCoords(lat, lng);
          setStatus('granted');
          resolve({ ok: true, coords: { lat, lng } });
        },
        (err) => {
          setStatus(err.code === 1 ? 'denied' : 'error');
          setError(err);
          resolve({ ok: false, error: err });
        },
        { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
      );
    });
  }, [promptAllowed, saveCoords]);

  // Check current permission and silently hydrate coords if already granted.
  // ✅ ESLint: include `coords` so the effect stays correct when coords change.
  useEffect(() => {
    let cancelled = false;

    const finish = () => {
      if (!cancelled) setBootstrapped(true);
    };

    // Load server config for geolocation prompt (via /api/health)
    (async () => {
      try {
        const r = await fetch('/api/health', { credentials: 'include' });
        const js = await r.json().catch(() => ({}));
        if (!cancelled) setPromptAllowed(!!js.geoConsentPromptEnabled);
      } catch (_) {
        // default remains false (disabled)
      }
    })();

    if (typeof navigator === 'undefined') {
      setStatus('error');
      setError(new Error('Navigator not available'));
      finish();
      return;
    }

    // If Permissions API available, prefer it.
    const checkWithPermissionsAPI = async () => {
      try {
        if (!navigator.permissions || !navigator.permissions.query) {
          // Fallback: we can’t know without prompting; keep prior coords if any.
          setStatus(coords ? 'granted' : 'idle');
          finish();
          return;
        }

        const perm = await navigator.permissions.query({ name: 'geolocation' });
        if (cancelled) return;

        const handleState = (state) => {
          if (state === 'granted') {
            // If we already have coords, don't refetch repeatedly on every rerun.
            if (coords) {
              setStatus('granted');
              finish();
              return;
            }
            setStatus('requesting');
            navigator.geolocation.getCurrentPosition(
              (pos) => {
                if (cancelled) return;
                const lat = pos.coords.latitude;
                const lng = pos.coords.longitude;
                saveCoords(lat, lng);
                setStatus('granted');
                finish();
              },
              (err) => {
                if (cancelled) return;
                setStatus('error');
                setError(err);
                finish();
              },
              { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
            );
          } else if (state === 'denied') {
            setStatus('denied');
            finish();
          } else {
            // 'prompt'
            // Keep any cached coords for instant UI; user can decide via modal.
            setStatus(coords ? 'granted' : 'idle');
            finish();
          }
        };

        handleState(perm.state);
        // reflect changes if user flips the browser setting while app is open
        perm.onchange = () => handleState(perm.state);
      } catch (e) {
        // Fallback if query throws
        setStatus(coords ? 'granted' : 'idle');
        finish();
      }
    };

    checkWithPermissionsAPI();
    return () => {
      cancelled = true;
    };
  }, [coords, saveCoords]); // 👈 include saveCoords for eslint

  const value = useMemo(
    () => ({
      coords,
      status,
      error,
      enableLocation,
      refreshLocation,
      geoEnabled: !!coords,
      bootstrapped,
      promptAllowed,
    }),
    [coords, status, error, enableLocation, refreshLocation, bootstrapped, promptAllowed]
  );

  return <GeoContext.Provider value={value}>{children}</GeoContext.Provider>;
}

export function useGeo() {
  const ctx = useContext(GeoContext);
  if (!ctx) throw new Error('useGeo must be used within GeoProvider');
  return ctx;
}
